import {
  AlignType,
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EModalVariants,
  ETextStyleVariant,
  GeneralCalendarIcon,
  GeneralCaretRightIcon,
  GeneralManageVehiclesOutlineIcon,
  GeneralMapPinOutlineIcon,
  Heading,
  Modal,
  Selector,
} from '@outdoorsyco/bonfire';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DayPickerProps } from 'react-day-picker';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { IAutocompleteOption } from '@/components/switchback/Autocomplete/AutocompleteOptions';
import { ESearchFilters } from '@/constants/searchFilters';
import { useRouter } from '@/hooks/useRouter';
import {
  IHeaderSearchFilterDates,
  IHeaderSearchFilterGuests,
  setHeaderSearchFilter,
} from '@/redux/modules/globalHeader';
import { setFormFilter } from '@/redux/modules/searchForm';
import { TRootState } from '@/redux/rootReducer';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';
import { EDeliveryOption } from '@/services/types/core/delivery.types';
import { formatDateFullRange } from '@/utility/format-date';
import { getParamAsString, getParams } from '@/utility/queryParams';

import { INavbarSearch, QUERYSTRING_DATE_FORMAT } from '../../SearchHeader';
import { MobileSearchAddressModalContent } from './MobileSearchAddressModalContent';
import { MobileSearchDatesModalContent } from './MobileSearchDatesModalContent';
import { MobileSearchGuestsModalContent } from './MobileSearchGuestsModalContent';

type TMobileSearchModalProps = {
  location?: { lat: number; lng: number };
  address: INavbarSearch['address'];
  dates: INavbarSearch['dates'];
  guests: INavbarSearch['guests'];
  isSearchModalOpen: boolean;
  onCloseHandler: () => void;
  onSubmit?: () => void;
  onChangeGuests?: (guests?: IHeaderSearchFilterGuests) => void;
  onChangeDates?: (dates?: IHeaderSearchFilterDates) => void;
  onChangeAddress?: (address?: string, addressOption?: any) => void;
  popularDestinations?: { label: string }[];
  recentSearches?: IAutocompleteOption[];
  handleRecentSearchNavigation?: (url: string) => void;
  maxRecentSearches: number;
  isGuestOccupancyAvailable?: boolean;
  hasVariant: boolean;
  disabledDays?: DayPickerProps['disabled'];
};

export enum EFilterContent {
  ADDRESS = 'address',
  DATES = 'dates',
  GUESTS = 'guests',
}

export const MobileSearchModal = ({
  location,
  address,
  dates,
  guests,
  isSearchModalOpen,
  onCloseHandler,
  onSubmit,
  onChangeGuests,
  onChangeDates,
  onChangeAddress,
  popularDestinations,
  recentSearches,
  handleRecentSearchNavigation,
  maxRecentSearches,
  isGuestOccupancyAvailable,
  hasVariant,
  disabledDays = [],
}: TMobileSearchModalProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const router = useRouter();
  const currentQueryParams = useSelector((state: TRootState) => state.queryParams);
  const { isMetaLoading, errorMeta, metaPreview } = useSelector(
    (state: TRootState) => state.search,
  );

  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );
  const isRvSearchPage = router.pathname === '/rv-search';

  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [filterContent, setFilterContent] = useState<EFilterContent | null>(null);

  const isDeliveryMode =
    currentQueryParams?.delivery === 'true' &&
    currentQueryParams?.deliveryStationary === 'stationary';

  // Local state to track selected mode within the modal
  const [localIsDeliveryMode, setLocalIsDeliveryMode] = useState(isDeliveryMode);

  // Track initial values to detect changes
  const initialValuesRef = useRef({
    address,
    dates,
    guests,
    isDeliveryMode,
  });

  // Update initial values when modal opens
  useEffect(() => {
    if (isSearchModalOpen) {
      initialValuesRef.current = {
        address,
        dates,
        guests,
        isDeliveryMode,
      };
      // Sync local state with current query params when modal opens
      setLocalIsDeliveryMode(isDeliveryMode);
    }
  }, [isSearchModalOpen, address, dates, guests, isDeliveryMode]);

  // Check if any values have changed from initial state
  const hasChanges = useMemo(() => {
    const initial = initialValuesRef.current;

    // Check address change
    if (address !== initial.address) return true;

    // Check delivery mode change
    if (localIsDeliveryMode !== initial.isDeliveryMode) return true;

    // Check dates change
    const datesChanged =
      dates?.from?.getTime() !== initial.dates?.from?.getTime() ||
      dates?.to?.getTime() !== initial.dates?.to?.getTime() ||
      dates?.flexible_days !== initial.dates?.flexible_days;
    if (datesChanged) return true;

    // Check guests change
    const guestsChanged =
      guests?.adults !== initial.guests?.adults ||
      guests?.children !== initial.guests?.children ||
      guests?.infants !== initial.guests?.infants ||
      guests?.accessible !== initial.guests?.accessible ||
      guests?.pet_friendly !== initial.guests?.pet_friendly ||
      guests?.sleeps !== initial.guests?.sleeps ||
      guests?.seatbelts !== initial.guests?.seatbelts;
    if (guestsChanged) return true;

    return false;
  }, [address, dates, guests, localIsDeliveryMode]);

  const handleModeSelect = (isDelivery: boolean) => {
    // Update local state immediately for UI feedback
    setLocalIsDeliveryMode(isDelivery);

    // Update form filter for preview only, don't trigger actual search
    const filters: Record<string, any> = {};

    // Add current form values to filters
    if (address) {
      filters[ESearchFilters.ADDRESS] = address;
    }

    if (dates?.from && dates?.to) {
      filters[ESearchFilters.DATE_FROM] = dayjs(dates.from).format(QUERYSTRING_DATE_FORMAT);
      filters[ESearchFilters.DATE_TO] = dayjs(dates.to).format(QUERYSTRING_DATE_FORMAT);
      if (dates.flexible_days) {
        filters[ESearchFilters.FLEXIBLE_DAYS] = dates.flexible_days;
      }
    }

    if (guests?.adults) {
      filters[ESearchFilters.GUESTS_ADULTS] = guests.adults;
    }
    if (guests?.children) {
      filters[ESearchFilters.GUESTS_CHILDREN] = guests.children;
    }
    if (guests?.infants) {
      filters[ESearchFilters.GUESTS_INFANTS] = guests.infants;
    }
    if (guests?.sleeps) {
      filters[ESearchFilters.SLEEPS] = guests.sleeps;
    }
    if (guests?.seatbelts) {
      filters[ESearchFilters.SEATBELTS] = guests.seatbelts;
    }

    // Add or remove delivery mode based on selection
    if (isDelivery) {
      filters[ESearchFilters.DELIVERY] = 'true';
      filters[ESearchFilters.DELIVERY_STATIONARY] = EDeliveryOption.STATIONARY;
    } else {
      filters[ESearchFilters.DELIVERY] = undefined;
      filters[ESearchFilters.DELIVERY_STATIONARY] = undefined;
    }

    // Update form filter for preview only (this won't trigger the actual search)
    dispatch(
      setFormFilter({
        filters,
        filterMethod: null, // Indicates it's not from direct user input
        ignorePersistedFilters: true,
      }),
    );
  };

  // Trigger search meta preview when form values change (matching desktop filter behavior)
  useEffect(() => {
    if (!isSearchModalOpen) return; // Only update when modal is open

    const filters: Record<string, any> = {};

    // Add current form values to filters
    if (address) {
      filters[ESearchFilters.ADDRESS] = address;
    }

    if (dates?.from && dates?.to) {
      filters[ESearchFilters.DATE_FROM] = dayjs(dates.from).format(QUERYSTRING_DATE_FORMAT);
      filters[ESearchFilters.DATE_TO] = dayjs(dates.to).format(QUERYSTRING_DATE_FORMAT);
      if (dates.flexible_days) {
        filters[ESearchFilters.FLEXIBLE_DAYS] = dates.flexible_days;
      } else {
        filters[ESearchFilters.FLEXIBLE_DAYS] = undefined;
      }
    } else {
      // Clear date filters when dates are cleared
      filters[ESearchFilters.DATE_FROM] = undefined;
      filters[ESearchFilters.DATE_TO] = undefined;
      filters[ESearchFilters.FLEXIBLE_DAYS] = undefined;
    }

    if (guests?.adults) {
      filters[ESearchFilters.GUESTS_ADULTS] = guests.adults;
    }
    if (guests?.children) {
      filters[ESearchFilters.GUESTS_CHILDREN] = guests.children;
    } else {
      filters[ESearchFilters.GUESTS_CHILDREN] = undefined;
    }
    if (guests?.infants) {
      filters[ESearchFilters.GUESTS_INFANTS] = guests.infants;
    }
    if (guests?.sleeps) {
      filters[ESearchFilters.SLEEPS] = guests.sleeps;
    }
    if (guests?.seatbelts) {
      filters[ESearchFilters.SEATBELTS] = guests.seatbelts;
    }

    // Add delivery mode if enabled
    if (localIsDeliveryMode) {
      filters[ESearchFilters.DELIVERY] = 'true';
      filters[ESearchFilters.DELIVERY_STATIONARY] = EDeliveryOption.STATIONARY;
    }

    // Only trigger update if we have meaningful filters
    if (Object.keys(filters).length > 0) {
      dispatch(
        setFormFilter({
          filters,
          filterMethod: null, // Indicates it's not from direct user input
          ignorePersistedFilters: true,
        }),
      );
    }
  }, [dispatch, isSearchModalOpen, address, dates, guests, localIsDeliveryMode]);

  const onClose = () => {
    const initialParams = getParams(router);
    const initialAddress: string | undefined = initialParams[ESearchFilters.ADDRESS]
      ? initialParams[ESearchFilters.ADDRESS]?.toString()
      : undefined;

    const initialDateFrom = initialParams[ESearchFilters.DATE_FROM];
    const initialDateTo = initialParams[ESearchFilters.DATE_TO];
    const initialFlexibleDays = initialParams[ESearchFilters.FLEXIBLE_DAYS];
    const initialDates: IHeaderSearchFilterDates | undefined =
      initialDateFrom && initialDateTo
        ? {
            from: dayjs(initialDateFrom.toString(), QUERYSTRING_DATE_FORMAT).toDate(),
            to: dayjs(initialDateTo.toString(), QUERYSTRING_DATE_FORMAT).toDate(),
            flexible_days:
              initialFlexibleDays === undefined ? undefined : Number(initialFlexibleDays),
          }
        : undefined;

    const initialGuestsAdults = initialParams[ESearchFilters.GUESTS_ADULTS]
      ? parseInt(String(initialParams[ESearchFilters.GUESTS_ADULTS]), 10)
      : undefined;
    const initialGuestsChildren = initialParams[ESearchFilters.GUESTS_CHILDREN]
      ? parseInt(String(initialParams[ESearchFilters.GUESTS_CHILDREN]), 10)
      : undefined;
    const initialGuestsInfants = initialParams[ESearchFilters.GUESTS_INFANTS]
      ? parseInt(String(initialParams[ESearchFilters.GUESTS_INFANTS]), 10)
      : undefined;
    const initialGuestsSeatbelts = initialParams[ESearchFilters.SEATBELTS]
      ? parseInt(String(initialParams[ESearchFilters.SEATBELTS]), 10)
      : undefined;

    let initialGuestsSleeps;
    if (initialParams[ESearchFilters.SLEEPS]) {
      initialGuestsSleeps = parseInt(String(initialParams[ESearchFilters.SLEEPS]), 10);
    } else if (
      initialParams[ESearchFilters.GUESTS_ADULTS] ||
      initialParams[ESearchFilters.GUESTS_CHILDREN]
    ) {
      initialGuestsSleeps = Math.min(
        parseInt(String(initialParams[ESearchFilters.GUESTS_ADULTS]), 10) +
          parseInt(String(initialParams[ESearchFilters.GUESTS_CHILDREN]), 10),
        10,
      );
    } else {
      initialGuestsSleeps = undefined;
    }

    const initialFeatures = getParamAsString(initialParams[ESearchFilters.FILTER_FEATURE]);
    const initialGuestsFeatures = initialFeatures
      ? {
          accessible: initialFeatures.includes('accessible'),
          pet_friendly: initialFeatures.includes('pet_friendly'),
        }
      : undefined;

    const initialState = {
      address: initialAddress,
      dates: initialDates,
      guests: {
        adults: initialGuestsAdults,
        children: initialGuestsChildren,
        infants: initialGuestsInfants,
        seatbelts: initialGuestsSeatbelts,
        sleeps: initialGuestsSleeps,
        ...initialGuestsFeatures,
      },
    };

    dispatch(setHeaderSearchFilter(initialState));
    onCloseHandler();
  };

  const handleSubmit = () => {
    if (filterPreviewState === 'loading') return;
    onSubmit?.();
    onCloseHandler();
  };

  const formattedDates = useMemo<string | null>(() => {
    if (!dates?.from || !dates?.to) {
      return null;
    }

    const datesLabel = formatDateFullRange(dates.from.toString(), dates.to.toString()) || '';

    if (dates.flexible_days) {
      return `${datesLabel} (±${dates.flexible_days})`;
    }

    return datesLabel;
  }, [dates]);

  const totalGuests = (guests?.adults || 0) + (guests?.children || 0) + (guests?.infants || 0) || 0;
  const guestsLabel = intl.formatMessage(
    {
      defaultMessage: '{totalGuests, plural, one {# guest} other {# guests}}',
      id: 'ErPlEg',
      description: 'Global Header > label for guests button on mobile bar',
    },
    {
      totalGuests,
    },
  );

  const datesLabel = formattedDates
    ? formattedDates
    : intl.formatMessage({
        defaultMessage: 'Add dates',
        id: 'zP42BU',
        description: 'Global Header > Placeholder text for dates button on mobile bar',
      });

  const getLocationLabel = () => {
    if (!isRentalsVDeliverySearchModeEnabled) {
      return intl.formatMessage({ defaultMessage: 'Location', id: 'rvirM2' });
    }

    return localIsDeliveryMode
      ? intl.formatMessage({
          defaultMessage: 'Delivery location',
          id: 'gD5bLT',
          description: 'Location label for delivery mode',
        })
      : intl.formatMessage({
          defaultMessage: 'Pickup location',
          id: 'yHQgLI',
          description: 'Location label for RV rental mode',
        });
  };

  const getLocationDescription = () => {
    if (address?.split('•').map(part => part.trim())[0]) {
      return address.split('•').map(part => part.trim())[0];
    }

    if (!isRentalsVDeliverySearchModeEnabled) {
      return intl.formatMessage({ defaultMessage: 'Add location', id: 'XOc92d' });
    }

    return localIsDeliveryMode
      ? intl.formatMessage({
          defaultMessage: 'Add a location',
          id: 'le38Ar',
          description: 'Add location placeholder for delivery mode',
        })
      : intl.formatMessage({
          defaultMessage: 'Add a pickup location',
          id: 'Tfw1wl',
          description: 'Add pickup location placeholder for RV rental mode',
        });
  };

  const onSelectFilterContentHandler = (selectedFilterContent: EFilterContent) => {
    setOpenFilterModal(true);
    setFilterContent(selectedFilterContent);
  };

  // Get filter preview state (matching desktop logic)
  let filterPreviewState: 'idle' | 'loading' | 'error' = 'idle';
  if (isMetaLoading) filterPreviewState = 'loading';
  if (errorMeta) filterPreviewState = 'error';
  const total = metaPreview?.total !== undefined ? metaPreview?.total : null;

  // Get button text based on search results (matching desktop logic)
  const getButtonText = () => {
    const resultsMessage = intl.formatMessage(
      {
        defaultMessage: 'Show {total, plural, one {# RV} other {# RVs}}',
        id: 'JZeWmw',
        description: 'Search > Filters: label with preview results',
      },
      { total },
    );
    const defaultMessage = intl.formatMessage({
      defaultMessage: 'Show RVs',
      id: 'iYo5qy',
      description: 'Search > Filters: default label',
    });
    const noResultsMessage = intl.formatMessage({
      defaultMessage: 'No results',
      id: 'Ukeqf0',
      description: 'Search > Filters: label with no results',
    });

    // eslint-disable-next-line no-nested-ternary
    return total !== null ? (total > 0 ? resultsMessage : noResultsMessage) : defaultMessage;
  };

  // Get disabled state (matching desktop logic combined with change detection)
  const isButtonDisabled = (filterPreviewState === 'idle' && total === 0) || !hasChanges;

  return (
    <>
      <Modal
        isOpen={isSearchModalOpen}
        onClose={onClose}
        variant={EModalVariants.Bottom}
        className="z-[1410]">
        <Modal.Actions className="top-5" />
        <Modal.Header inlineHeader={true} className="mb-8 !px-8 !pt-[26px] !pb-0">
          <Heading variant={EHeadingStyleVariant.H6}>
            <FormattedMessage defaultMessage="Trip information" id="MrJVmR" />
          </Heading>
        </Modal.Header>
        <Modal.Content className="!mb-0 !px-8">
          <div className="flex flex-col gap-8">
            {isRentalsVDeliverySearchModeEnabled && isRvSearchPage && (
              <div className="flex gap-3">
                {/* Rentals Mode */}
                <Selector
                  alignVariant={AlignType.VerticalLeft}
                  ariaLabel="Rentals mode"
                  className="flex-1"
                  contentWrapperClassName="!p-3"
                  description={intl.formatMessage({
                    defaultMessage: 'RVs to drive or tow',
                    id: 'Jy3eQB',
                    description: 'Rentals mode description',
                  })}
                  descriptionVariant={ETextStyleVariant.SmallRegular}
                  icon={GeneralManageVehiclesOutlineIcon}
                  isSelected={!localIsDeliveryMode}
                  label={intl.formatMessage({
                    defaultMessage: 'Rentals',
                    id: 'WOphE2',
                    description: 'Rentals mode option',
                  })}
                  labelClassName="!text-sm font-medium"
                  labelVariant={ETextStyleVariant.MediumBold}
                  name="rentals-mode"
                  onChange={() => handleModeSelect(false)}
                  value="rentals"
                  withBorder
                />

                {/* Delivery Mode */}
                <Selector
                  alignVariant={AlignType.VerticalLeft}
                  ariaLabel="Delivery mode"
                  className="flex-1"
                  contentWrapperClassName="!p-3"
                  description={intl.formatMessage({
                    defaultMessage: 'Ready-to-stay RVs',
                    id: 'oGDsjD',
                    description: 'Delivery mode description',
                  })}
                  descriptionVariant={ETextStyleVariant.SmallRegular}
                  icon={GeneralMapPinOutlineIcon}
                  isSelected={localIsDeliveryMode}
                  label={intl.formatMessage({
                    defaultMessage: 'Delivery',
                    id: 'oxGlxs',
                    description: 'Delivery mode option',
                  })}
                  labelClassName="font-medium !text-sm"
                  labelVariant={ETextStyleVariant.MediumBold}
                  name="delivery-mode"
                  onChange={() => handleModeSelect(true)}
                  value="delivery"
                  withBorder
                />
              </div>
            )}
            {!hasVariant && (
              <Selector
                name="location"
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'Please select a location.',
                  id: '1h5k89',
                })}
                label={getLocationLabel()}
                description={getLocationDescription()}
                value={address || ''}
                isSelected={!!address}
                onChange={() => onSelectFilterContentHandler(EFilterContent.ADDRESS)}
                className="[&>input]:h-0"
                contentWrapperClassName="!p-4"
                descriptionVariant={ETextStyleVariant.MediumBold}
                labelClassName="!text-sm text-gray-50"
              />
            )}
            <Selector
              name="dates"
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Please select some dates.',
                id: '/1Vehk',
              })}
              label={intl.formatMessage({
                defaultMessage: 'Dates',
                id: 'D6b0Au',
              })}
              description={datesLabel}
              value={formattedDates || ''}
              isSelected={!!formattedDates}
              onChange={() => onSelectFilterContentHandler(EFilterContent.DATES)}
              className="[&>input]:h-0"
              contentWrapperClassName="!p-4 [&>div]:w-full [&>div]:justify-between"
              descriptionVariant={ETextStyleVariant.MediumBold}
              labelClassName="!text-sm text-gray-50"
              icon={GeneralCalendarIcon}
              alignVariant={AlignType.HorizontalRight}
            />
            <Selector
              name="guests"
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Please select some guests.',
                id: 'wGpMIJ',
              })}
              label={intl.formatMessage({ defaultMessage: 'Guest', id: 'zF7Cth' })}
              description={
                totalGuests
                  ? guestsLabel
                  : intl.formatMessage({ defaultMessage: 'Add guest details', id: 'Wo6T+x' })
              }
              value={guestsLabel || ''}
              isSelected={!!totalGuests}
              onChange={() => onSelectFilterContentHandler(EFilterContent.GUESTS)}
              className="[&>input]:h-0"
              contentWrapperClassName="!p-4 [&>div]:w-full [&>div]:justify-between"
              descriptionVariant={ETextStyleVariant.MediumBold}
              labelClassName="!text-sm text-gray-50"
              icon={GeneralCaretRightIcon}
              alignVariant={AlignType.HorizontalRight}
            />
          </div>

          <div className="flex justify-end w-full pt-8 pb-8">
            <Button
              variant={EButtonColorVariant.Primary}
              label={getButtonText()}
              onClick={handleSubmit}
              loading={filterPreviewState === 'loading'}
              disabled={isButtonDisabled}
            />
          </div>
        </Modal.Content>
      </Modal>

      {openFilterModal && filterContent && (
        <Modal
          isOpen={openFilterModal}
          onClose={() => setOpenFilterModal(false)}
          variant={EModalVariants.Bottom}
          className="z-[1410]"
          dialogClassName={filterContent !== EFilterContent.DATES ? 'p-8' : ''}>
          {filterContent === EFilterContent.ADDRESS && (
            <MobileSearchAddressModalContent
              onCloseHandler={() => setOpenFilterModal(false)}
              location={location}
              address={address}
              onChangeAddress={onChangeAddress}
              popularDestinations={popularDestinations}
              recentSearches={recentSearches}
              handleRecentSearchNavigation={handleRecentSearchNavigation}
              maxRecentSearches={maxRecentSearches}
            />
          )}
          {filterContent === EFilterContent.DATES && (
            <MobileSearchDatesModalContent
              onCloseHandler={() => setOpenFilterModal(false)}
              dates={dates}
              onChangeDates={onChangeDates}
              disabledDays={disabledDays}
            />
          )}
          {filterContent === EFilterContent.GUESTS && (
            <MobileSearchGuestsModalContent
              onCloseHandler={() => setOpenFilterModal(false)}
              guests={guests}
              onChangeGuests={onChangeGuests}
              isGuestOccupancyAvailable={isGuestOccupancyAvailable}
            />
          )}
        </Modal>
      )}
    </>
  );
};
